import type { GalleryData } from './types';
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { BentoZeroState } from './bento-states/zero-state';
import { BentoBannerState } from './bento-states/banner-state';
import { BentoSplitState } from './bento-states/split-state';
import { BentoFourState } from './bento-states/four-state';
import { BentoFiveState } from './bento-states/five-state';
import { BentoGridState } from './bento-states/grid-state';
import { bentoTokens } from './bento-tokens';

interface BentoVariantProps {
  data: GalleryData;
  onImageClick?: (index: number) => void;
  onSeeAllClick?: () => void;
  blur?: boolean;
  badgesSlot?: ReactNode;
  seeAllSlot?: (total: number) => ReactNode;
  mapSlot?: ReactNode;
  placeholderSlot?: ReactNode;
}

/**
 * Bento Variant - Adaptive Gallery Layout
 * 
 * Orchestrates different layout states based on image count:
 * - N=0: Zero state with placeholder
 * - N=1: Banner state (full-width image)
 * - N=2-3: Split state (60/40 layout)
 * - N=4: Four state (main + 2 squares + long)
 * - N=5: Five state (main + 2x2 squares)
 * - N≥6: Grid state (8-2-2 bento grid)
 */
export function BentoVariant({
  data,
  onImageClick,
  onSeeAllClick,
  blur,
  badgesSlot,
  seeAllSlot,
  mapSlot,
  placeholderSlot,
}: BentoVariantProps) {
  const { images, descriptions, total, altPrefix = 'Image' } = data;
  const imageCount = images?.length || 0;

  const getAlt = (index: number) => {
    const indexStr = index.toString();
    return descriptions?.has(indexStr)
      ? `${descriptions.get(indexStr)} | Image ${index + 1}`
      : `${altPrefix} | Image ${index + 1}`;
  };

  // Wrap all states with container spacing
  return (
    <Box
      sx={{
        paddingTop: bentoTokens.container.paddingTop,
        paddingLeft: bentoTokens.container.paddingHorizontal,
        paddingRight: bentoTokens.container.paddingHorizontal,
        marginBottom: bentoTokens.container.marginBottom,
        width: '100%',
      }}
    >
      {/* State 1: Zero State (N=0) */}
      {imageCount === 0 && (
        <BentoZeroState
          placeholderSlot={placeholderSlot}
          badgesSlot={badgesSlot}
          blur={blur}
        />
      )}

      {/* State 2: Banner State (N=1) */}
      {imageCount === 1 && (
        <BentoBannerState
          imageUrl={images[0]}
          alt={getAlt(0)}
          onImageClick={() => onImageClick?.(0)}
          blur={blur}
          badgesSlot={badgesSlot}
        />
      )}

      {/* State 3: Split State (N=2 or 3) */}
      {imageCount >= 2 && imageCount <= 3 && (
        <BentoSplitState
          images={images}
          getAlt={getAlt}
          onImageClick={onImageClick}
          blur={blur}
          badgesSlot={badgesSlot}
          seeAllSlot={seeAllSlot}
          mapSlot={mapSlot}
          onSeeAllClick={onSeeAllClick}
          total={total}
        />
      )}

      {/* State 4: Four State (N=4) */}
      {imageCount === 4 && (
        <BentoFourState
          images={images}
          getAlt={getAlt}
          onImageClick={onImageClick}
          blur={blur}
          badgesSlot={badgesSlot}
          seeAllSlot={seeAllSlot}
          total={total}
        />
      )}

      {/* State 5: Five State (N=5) */}
      {imageCount === 5 && (
        <BentoFiveState
          images={images}
          getAlt={getAlt}
          onImageClick={onImageClick}
          blur={blur}
          badgesSlot={badgesSlot}
          seeAllSlot={seeAllSlot}
          total={total}
        />
      )}

      {/* State 6: Grid State (N ≥ 6) */}
      {imageCount >= 6 && (
        <BentoGridState
          images={images}
          getAlt={getAlt}
          onImageClick={onImageClick}
          blur={blur}
          badgesSlot={badgesSlot}
          seeAllSlot={seeAllSlot}
          mapSlot={mapSlot}
          onSeeAllClick={onSeeAllClick}
          total={total}
        />
      )}
    </Box>
  );
}
